import { MultiListBoxItem } from '../../cpq-multi-list-box-with-selection/multi-list-box-item.model';
import { PermissionGroupCollection } from '../../cpq-permission-groups-selection/cpq-permission-group-selection.model';
import { SelectedPermissions } from '../../../users-module/permission-groups-data-table/permission-group.model';
import { ILookup } from '../interfaces/ILookup.interface';
import { environment } from '../../../../environments/environment';

/*
Used in converting enums to ILookup (weather id will be number or string)
 */
export enum LookupTypeEnum {
  String = 0,
  Number = 1,
}

/**
   * navigate to old (aspx) page
   * @param url Ex. UsersImport.aspx
   */
export function navigateToOldSetup(url: string, openInNewTab = false) {
  if (openInNewTab) {
    window.open(`${window.location.origin}/admin/${url}`);
    return;
  }

  window.location.href = `${window.location.origin}/admin/${url}`;
}

export function openConfigurationPreview(productId: number, securityKey: string, quoteId = null) {
  window.open(`${environment.url}/Configurator.aspx?pid=${productId}&UseFirstWorkflowTab=1&sid=${securityKey}${quoteId ? "&quoteId=" + quoteId : ""}&isInPreviewMode=true`, null, 'width=1000,height=700,menubar=no,location=no,resizable=yes,scrollbars=yes,status=no');
}

export function checkArrayForDuplicates(array: Array<string>): boolean {
    const seen = new Set();
    for (const item of array) {
      if (seen.has(item)) {
        return true;
      }
      seen.add(item);
    }
    return false;
  }
  
export function clone<T>(originalObject: T, circular = false): T {
  // First create an empty object with
  // same prototype of our original source

  let propertyIndex,
    descriptor,
    keys,
    current,
    nextSource,
    indexOf;
  const copies = [{
    source: originalObject,
    target: Object.create(Object.getPrototypeOf(originalObject))
  }],
    cloneObject = copies[0].target,
    sourceReferences = [originalObject],
    targetReferences = [cloneObject];

  // First in, first out
  while (current = copies.shift()) {
    keys = Object.getOwnPropertyNames(current.source);

    for (propertyIndex = 0; propertyIndex < keys.length; propertyIndex++) {
      // Save the source's descriptor
      descriptor = Object.getOwnPropertyDescriptor(current.source, keys[propertyIndex]);

      if (!descriptor.value || typeof descriptor.value !== 'object') {
        Object.defineProperty(current.target, keys[propertyIndex], descriptor);
        continue;
      }

      nextSource = descriptor.value;
      descriptor.value = Array.isArray(nextSource) ?
        [] :
        Object.create(Object.getPrototypeOf(nextSource));

      if (circular) {
        indexOf = sourceReferences.indexOf(nextSource);

        if (indexOf !== -1) {
          // The source is already referenced, just assign reference
          descriptor.value = targetReferences[indexOf];
          Object.defineProperty(current.target, keys[propertyIndex], descriptor);
          continue;
        }

        sourceReferences.push(nextSource);
        targetReferences.push(descriptor.value);
      }

      Object.defineProperty(current.target, keys[propertyIndex], descriptor);

      copies.push({ source: nextSource, target: descriptor.value });
    }
  }

  return cloneObject;
}

/*
usage: enumToLookups<string>(DataTypeEnum) // in case you want to return Array<ILookup<string, string>>
       enumToLookups<number>(DataTypeEnum, LookupTypeEnum.Number) // in case you want to return Array<ILookup<number, string>>
 */
export function enumToLookups<T>(_enum, type = LookupTypeEnum.String): Array<ILookup<T, string>> {
  let keys = new Array<ILookup<any, string>>();
  
  for (const key in _enum) {
    if (!isNaN(Number(key))) {
      if (type === LookupTypeEnum.String) {
        keys.push({ id: key, text: _enum[key] });
      }
      else if (type === LookupTypeEnum.Number) {
        keys.push({ id: +key, text: _enum[key] });
      }
    }
  }
  
  return keys;
}

export function getSelectedPermissions(permissionGroupCollection: PermissionGroupCollection, selectedPermissions: SelectedPermissions): Array<MultiListBoxItem> {
  return [].concat(permissionGroupCollection.companyPermissions.children.filter(x => selectedPermissions.companyPermissions.includes(x.id))
    , permissionGroupCollection.manualPermissions.children.filter(x => selectedPermissions.manualPermissions.includes(x.id))
    , permissionGroupCollection.marketPermissions ? permissionGroupCollection.marketPermissions.children.filter(x => selectedPermissions.marketPermissions.includes(x.id)) : []
    , permissionGroupCollection.salesOrgPermissions ? permissionGroupCollection.salesOrgPermissions.children.filter(x => selectedPermissions.salesOrgPermissions.includes(x.id)) : []
    , permissionGroupCollection.multiBrandingPermissions.children.filter(x => selectedPermissions.multiBrandingPermissions.includes(x.id))
    , permissionGroupCollection.userTypePermissions.children.filter(x => selectedPermissions.userTypePermissions.includes(x.id)));
}

export function getSelectedUniquePermissions(permissionGroupCollection: PermissionGroupCollection, selectedPermissionsIds: Array<number>): Array<MultiListBoxItem> {
  const clonedGroup = clone<PermissionGroupCollection>(permissionGroupCollection);
  return [].concat(clonedGroup.companyPermissions.children.filter(x => selectedPermissionsIds.includes(x.id))
    , clonedGroup.manualPermissions.children.filter(x => selectedPermissionsIds.includes(x.id))
    , clonedGroup.marketPermissions ? clonedGroup.marketPermissions.children.filter(x => selectedPermissionsIds.includes(x.id)) : []
    , clonedGroup.salesOrgPermissions ? clonedGroup.salesOrgPermissions.children.filter(x => selectedPermissionsIds.includes(x.id)) : []
    , clonedGroup.multiBrandingPermissions.children.filter(x => selectedPermissionsIds.includes(x.id))
    , clonedGroup.users.children.filter(x => selectedPermissionsIds.includes(x.id))
    , clonedGroup.userTypePermissions.children.filter(x => selectedPermissionsIds.includes(x.id)));
}

export function getSelectedUserTypePermissions(permissionGroupCollection: PermissionGroupCollection, selectedPermissionsIds: Array<number>): Array<MultiListBoxItem> {
  const clonedGroup = clone<PermissionGroupCollection>(permissionGroupCollection);
  return [].concat(clonedGroup.userTypePermissions.children.filter(x => selectedPermissionsIds.includes(x.id)));
}

export function getSelectedCopmanyTypePermissions(permissionGroupCollection: PermissionGroupCollection, selectedPermissionsIds: Array<number>): Array<MultiListBoxItem> {
  const clonedGroup = clone<PermissionGroupCollection>(permissionGroupCollection);
  return [].concat(clonedGroup.companyPermissions.children.filter(x => selectedPermissionsIds.includes(x.id)));
}

export function setModalWindowSize(node, tableWidth) {
  node.setAttribute('style', `width: ${tableWidth.toString()}px`);
}

/** is Internet Explorer or MS Edge */
export function isIE() {
  return document['documentMode'] || /Edge/.test(navigator.userAgent);
}

export function getEnvironmentUrl() : string
{
  return window.location.host;
}

export function generateUniqueId(text: string): string {
  let dateUniqueString = new Date().valueOf();
  return text + "_" + dateUniqueString;
}

/** used to set a 'top' property for 'fixed' virtual-scroll-viewport in order to have correct position and to be placed in front of the dialog
    element with class "cdk-virtual-scroll-viewport" must have a style: position: fixed in order for this function to work correctly - 
    for the example please see 'styles.scss' file
*/
export function fixCpqSelectSuggestionDiv(id: string)
{
  let element = document.querySelector('#'+id);
  if (element) {
    element.addEventListener('click', (event: Event) => {
      let node = event.target as HTMLElement
      let yPostition = node.getBoundingClientRect().y + node.getBoundingClientRect().height;
      let floorElements = document.getElementsByClassName("cdk-virtual-scroll-viewport") as HTMLCollectionOf<HTMLElement>;
      if (floorElements[0]) {
        floorElements[0].style.top = "" + yPostition + "px";
      }
    });
  }
}